package com.klantenstop.web.data.models.domain

/**
 * The 10 Belgian provinces. Mirrors the backend's fixed `Province` enum (not a database table).
 * Each province has an explicit [slug] that matches the backend's stable identifier and a
 * Dutch [displayName] for use in the UI (dropdowns, grouped lists, ...).
 *
 * [slug] is NOT used for the `stad` search param (see [citySearchSlug] below) -- it's only used
 * to assign/display a province (e.g. `CuratedCity.province`).
 *
 * [citySearchSlug] is the identifier that must be sent as the `stad` search param to select this
 * province's bounding-box search mode on the backend. A curated city can share a province's
 * natural name (e.g. the city AND the province of "Antwerpen"), and `stad` resolves curated-city
 * slugs before province slugs, so reusing [slug] would make the province's bounding-box mode
 * unreachable once a same-named curated city exists. Must stay in sync with the backend's
 * `Province.citySearchSlug` (`"$slug-provincie"`).
 */
enum class Province(val slug: String, val displayName: String) {
    ANTWERPEN("antwerpen", "Antwerpen"),
    OOST_VLAANDEREN("oost-vlaanderen", "Oost-Vlaanderen"),
    WEST_VLAANDEREN("west-vlaanderen", "West-Vlaanderen"),
    VLAAMS_BRABANT("vlaams-brabant", "Vlaams-Brabant"),
    LIMBURG("limburg", "Limburg"),
    HENEGOUWEN("henegouwen", "Henegouwen"),
    LUIK("luik", "Luik"),
    BRUSSEL("brussel", "Brussel"),
    NAMEN("namen", "Namen"),
    WAALS_BRABANT("waals-brabant", "Waals-Brabant");

    // Separate from [slug] -- see the class doc for why province-assignment and province-search
    // need disjoint identifiers.
    val citySearchSlug: String get() = "$slug-provincie"

    companion object {
        fun fromSlug(slug: String): Province? = entries.find { it.slug == slug }

        /** Looks up a province by its [citySearchSlug] -- mirrors the backend's lookup used by `stad`. */
        fun fromCitySearchSlug(citySearchSlug: String): Province? =
            entries.find { it.citySearchSlug == citySearchSlug }
    }
}
